import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Clock, Paperclip, PhoneCall } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHero } from "@/components/site/PageHero";
import { SelectField, TextAreaField, TextField } from "@/components/common/Forms";
import { FileUpload } from "@/components/common/FileUpload";
import { SuccessPanel } from "@/components/common/SuccessPanel";
import { ChipGroup } from "@/components/home/QuoteChips";
import { ProductLines, findProduct, linesToItems } from "@/components/enquiry/ProductLines";
import { IMAGES, TIMELINES } from "@/data/site";
import { QUOTE_SERVICES, QUOTE_SITES, SPACES } from "@/data/spaces";

const Step = ({ n, title, children }) => (
  <section className="min-w-0 border-t border-slate-200 pt-7 first:border-t-0 first:pt-0" data-testid={`quote-step-${n}`}>
    <h2 className="mb-4 flex items-center gap-3 text-base font-semibold text-slate-900" style={{ fontFamily: "Inter, sans-serif" }}>
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#123634] text-xs text-white">{n}</span>{title}
    </h2>
    {children}
  </section>
);

const Extras = ({ f, set, files, setFiles }) => {
  const [open, setOpen] = useState(false);
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="quote-extras-toggle"><Paperclip className="h-4 w-4" /> Add a note or attach BOQ / drawings (optional)</button>;
  return (
    <div className="space-y-4" data-testid="quote-extras">
      <TextAreaField id="enq-message" label="Notes (optional)" rows={3} value={f.message} onChange={set("message")} placeholder="e.g. 3 floors and a basement, existing equipment to replace, tender reference" />
      <FileUpload id="enquiry-files" files={files} onChange={setFiles} label="BOQ, drawings or spec sheets (optional)" />
    </div>
  );
};

const validate = (f, items) => {
  if (!f.services.length) return "Choose Supply, AMC or both.";
  if (f.services.includes("Supply") && !items.length) return "Pick at least one product.";
  if (!f.property_type || !f.sites_count || !f.timeline) return "Please tell us the type of space, number of sites and timeline.";
  if (!f.name.trim() || !f.phone.trim() || !f.email.trim() || !f.company.trim() || !f.city.trim()) return "Please fill in your name, mobile, email, organisation and city.";
  if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return "Please enter a valid email address.";
  return "";
};

export default function BulkEnquiry() {
  const [params] = useSearchParams();
  const pre = findProduct(params.get("product") || "");
  const [f, setF] = useState({ services: pre ? ["Supply"] : [], property_type: "", sites_count: "", timeline: "", name: "", phone: "", email: "", company: "", city: "", message: "" });
  const [lines, setLines] = useState([{ id: pre ? pre.p.id : "", qty: "", text: "" }]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const supply = f.services.includes("Supply");

  const submit = async (e) => {
    e.preventDefault();
    const items = supply ? linesToItems(lines) : [];
    const msg = validate(f, items);
    setError(msg);
    if (msg) return;
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { ...f, kind: "bulk", subject: `Quote: ${f.services.join(" + ")}`, items, attachment_ids: files.map((x) => x.id) });
      setDone(data.reference);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bulk-enquiry-page">
      <PageHero eyebrow="Get a quote" title={pre ? `Get a quote for ${pre.p.name}` : "Get a quote in 2 minutes."} text="Tap what you need, tell us about the site, and a Vinsafe specialist calls you back within 1 business day. No checkout, no spam." image={IMAGES.corporate} />
      <section className="container-x py-14">
        {done ? (
          <SuccessPanel title="Quote request received" text="A Vinsafe specialist will call you within one business day with pricing." reference={done} actions={[{ to: "/", label: "Back to home" }, { to: "/solutions", label: "Browse products" }]} testid="enquiry-success" />
        ) : (
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <form onSubmit={submit} noValidate className="card-base min-w-0 space-y-7 p-6 sm:p-10 lg:col-span-2" data-testid="bulk-enquiry-form">
              <Step n="1" title="What do you need?">
                <ChipGroup label="Choose one or both" multi options={QUOTE_SERVICES} value={f.services} onChange={set("services")} testid="enq-services" />
                {supply && <div className="mt-5"><p className="mb-2 text-[13px] font-medium text-slate-700">Products & quantities <span className="text-[#C20500]">*</span></p><ProductLines lines={lines} setLines={setLines} /></div>}
              </Step>
              <Step n="2" title="About your site">
                <div className="space-y-5">
                  <SelectField id="enq-space" label="Type of space" required value={f.property_type} onChange={set("property_type")} options={SPACES.map((s) => s.name).concat("Other")} />
                  <ChipGroup label="Number of sites" options={QUOTE_SITES} value={f.sites_count} onChange={set("sites_count")} testid="enq-sites" />
                  <ChipGroup label="When do you need it?" options={TIMELINES} value={f.timeline} onChange={set("timeline")} testid="enq-timeline" />
                </div>
              </Step>
              <Step n="3" title="Where should we send the quote?">
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField id="enq-name" label="Your name" value={f.name} onChange={set("name")} required autoComplete="name" />
                  <TextField id="enq-phone" type="tel" label="Mobile / WhatsApp" value={f.phone} onChange={set("phone")} required autoComplete="tel" />
                  <TextField id="enq-email" type="email" label="Email" value={f.email} onChange={set("email")} required autoComplete="email" />
                  <TextField id="enq-company" label="Organisation" value={f.company} onChange={set("company")} required autoComplete="organization" />
                  <TextField id="enq-city" label="City" value={f.city} onChange={set("city")} required className="sm:col-span-2" />
                </div>
              </Step>
              <Extras f={f} set={set} files={files} setFiles={setFiles} />
              {error && <p className="text-sm text-[#C20500]" data-testid="bulk-enquiry-error">{error}</p>}
              <button type="submit" disabled={busy} className="btn-primary w-full px-8 py-3.5 sm:w-auto" data-testid="bulk-enquiry-submit-button">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Get my quote
              </button>
            </form>
            <aside className="space-y-4 lg:sticky lg:top-24">
              {[[Clock, "Reply within 1 business day", "A named specialist reviews your requirement."], [PhoneCall, "We call you", "Pricing is shared on a quick call or email, never public."]].map(([I, t, d]) => (
                <div key={t} className="card-base flex gap-4 p-5"><I className="h-5 w-5 shrink-0 text-[#C20500]" /><div><p className="font-semibold text-slate-900">{t}</p><p className="text-sm text-slate-600">{d}</p></div></div>
              ))}
            </aside>
          </div>
        )}
      </section>
    </div>
  );
}
